import { CHANNEL_COLORS, CHANNEL_LABEL, type DailyTotal } from './analytics.constants';

interface Props {
  dailyTotals: DailyTotal[];
  channels: string[];
  maxTotal: number;
  loading: boolean;
  error: string | null;
  formatDate: (d: string) => string;
}

export const AnalyticsBarChart = ({
  dailyTotals,
  channels,
  maxTotal,
  loading,
  error,
  formatDate,
}: Props) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
      <h3 className="text-sm font-semibold text-slate-900">Konversationer per dag</h3>
      <div className="flex items-center gap-4">
        {channels.map((ch) => (
          <div key={ch} className="flex items-center gap-1.5">
            <div
              className="h-3 w-3 rounded-sm"
              style={{ backgroundColor: CHANNEL_COLORS[ch] ?? '#94a3b8' }}
            />
            <span className="text-xs text-slate-500">{CHANNEL_LABEL[ch] ?? ch}</span>
          </div>
        ))}
      </div>
    </div>

    {loading ? (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Laddar...</div>
    ) : error ? (
      <div className="flex items-center justify-center h-48 text-red-500 text-sm">{error}</div>
    ) : dailyTotals.length === 0 ? (
      <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
        Ingen data for vald period.
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        <div className="flex items-end gap-0.5 h-48">
          {dailyTotals.map((day) => {
            const barHeightPct = (day.total / maxTotal) * 100;
            return (
              <div
                key={day.date}
                className="group flex-1 min-w-[4px] flex flex-col items-center justify-end h-full relative"
              >
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-xs text-white shadow-lg">
                  {formatDate(day.date)}: {day.total}
                </div>
                <div
                  className="w-full rounded-t flex flex-col-reverse overflow-hidden"
                  style={{ height: `${barHeightPct}%`, minHeight: day.total > 0 ? '2px' : '0' }}
                >
                  {channels.map((ch) => {
                    const chCount = day.byChannel[ch] ?? 0;
                    if (chCount === 0) return null;
                    return (
                      <div
                        key={ch}
                        style={{
                          height: `${(chCount / day.total) * 100}%`,
                          backgroundColor: CHANNEL_COLORS[ch] ?? '#94a3b8',
                          minHeight: '1px',
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-0.5">
          {dailyTotals.map((day, i) => {
            const step = Math.max(1, Math.ceil(dailyTotals.length / 10));
            const show = i === 0 || i === dailyTotals.length - 1 || i % step === 0;
            return (
              <div key={day.date} className="flex-1 min-w-[4px] text-center overflow-hidden">
                {show && (
                  <span className="text-[10px] text-slate-400 whitespace-nowrap">
                    {formatDate(day.date)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);
